import type { Observable } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';
import {
  isArdoqError,
  toArdoqError,
  type ArdoqError,
  type Result,
} from '@ardoq/common-helpers';
import { ResourceType } from '@ardoq/api-types';
import type {
  ArdoqId,
  FutureState,
  FutureStatePageResponse,
  FutureStateUpdatedResponse,
  FutureStateWebsocketEvent,
  Initiative,
  InitiativeSummary,
  InitiativeOwner,
  FutureStatePagePermissions,
} from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { scopeDataOperations } from '@ardoq/scope-data';
import { location$ } from 'router/declarative/location$';
import { filterRouteActive } from 'router/declarative/filterRouteActive';
import { api, transformationsApi } from '@ardoq/api';
import { reducedStream, streamReducer } from '@ardoq/rxbeach';
import { filterResourceType, websocket$ } from 'sync/websocket$';
import { websocketReducer } from 'streams/crud/reducers';
import { trackEventV2 } from 'tracking/tracking';
import { updateFutureState$ } from './updateFutureState$';
import { currentInitiativeSummary$ } from '../initiativePage/initiativePage$';
import { dispatchDetectedSetupStatus } from '../setupStatusDetection';
import { toFutureStatePageLoadEvents } from '../pageLoadTrackingEvents';
import type { TransformationsAccess } from '../writeInitiativeAccess';

export type FutureStatePage$ErrorState = {
  status: 'error';
  error: ArdoqError;
};

export const isErrorState = (
  state: FutureStatePage$State
): state is FutureStatePage$ErrorState => state.status === 'error';

export type FutureStatePage$LoadingState = { status: 'loading' };

export const isLoadingState = (
  state: FutureStatePage$State
): state is FutureStatePage$LoadingState => state.status === 'loading';

export type FutureStatePage$SuccessState = {
  status: 'success';
  futureState: FutureState;
  initiativeSummary: InitiativeSummary;
  owners: InitiativeOwner[];
  assignedTo: InitiativeOwner[];
  enhancedScopeData: EnhancedScopeData;
  permissions: FutureStatePagePermissions;
};

export type FutureStatePage$State =
  | FutureStatePage$LoadingState
  | FutureStatePage$SuccessState
  | FutureStatePage$ErrorState;

export type ActiveFutureStateIds = {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
};

export const FUTURE_STATE_ROUTE =
  '/transformations/initiatives/:initiativeId/future-states/:futureStateId';

const defaultState: FutureStatePage$State = { status: 'loading' };

const sameIds = (a: ActiveFutureStateIds, b: ActiveFutureStateIds): boolean =>
  a.initiativeId === b.initiativeId && a.futureStateId === b.futureStateId;

export const activeFutureStateIds$ = location$.pipe(
  filterRouteActive(FUTURE_STATE_ROUTE),
  map((location): ActiveFutureStateIds => ({
    initiativeId: location.params.initiativeId,
    futureStateId: location.params.futureStateId,
  })),
  distinctUntilChanged(sameIds),
  shareReplay({ bufferSize: 1, refCount: true })
);

const currentFutureState$: Observable<Result<FutureStatePageResponse>> =
  activeFutureStateIds$.pipe(
    switchMap(transformationsApi.fetchFutureState),
    tap(response => {
      dispatchDetectedSetupStatus(response);
      api.logErrorIfNeeded(response);
      if (!isArdoqError(response)) {
        toFutureStatePageLoadEvents(response.futureState).forEach(trackEventV2);
      }
    })
  );

export const setFutureState = (
  _state: FutureStatePage$State,
  futureStateResult: Result<FutureStatePageResponse>
): FutureStatePage$State => {
  if (isArdoqError(futureStateResult)) {
    return { status: 'error', error: futureStateResult };
  }

  return {
    status: 'success',
    futureState: futureStateResult.futureState,
    initiativeSummary: futureStateResult.initiativeSummary,
    owners: futureStateResult.futureState.owners ?? [],
    assignedTo: futureStateResult.futureState.assignedTo ?? [],
    enhancedScopeData: scopeDataOperations.enhance(futureStateResult.scopeData),
    permissions: futureStateResult.permissions,
  };
};

const toDeletedErrorState = (message: string): FutureStatePage$State => ({
  status: 'error',
  error: toArdoqError({ error: new Error(message) }),
});

export const applyInitiativeSummary = (
  state: FutureStatePage$State,
  summary: InitiativeSummary | null
): FutureStatePage$State => {
  if (
    state.status !== 'success' ||
    summary === null ||
    state.initiativeSummary._id !== summary._id
  ) {
    return state;
  }
  return { ...state, initiativeSummary: summary };
};

export const applyWebsocketInitiativeDelete = (
  state: FutureStatePage$State,
  initiativeId: ArdoqId
): FutureStatePage$State => {
  if (
    state.status !== 'success' ||
    state.initiativeSummary._id !== initiativeId
  ) {
    return state;
  }
  return toDeletedErrorState('Initiative deleted');
};

// The `_version` guard drops out-of-order events.
export const applyFutureStateUpdate = (
  state: FutureStatePage$State,
  { futureState, scopeData }: FutureStateWebsocketEvent
): FutureStatePage$State => {
  if (
    state.status !== 'success' ||
    state.futureState._id !== futureState._id ||
    futureState._version < state.futureState._version
  ) {
    return state;
  }
  return {
    ...state,
    futureState,
    owners: futureState.owners ?? [],
    assignedTo: futureState.assignedTo ?? [],
    enhancedScopeData: scopeDataOperations.enhance(scopeData),
  };
};

export const applyFutureStateDelete = (
  state: FutureStatePage$State,
  futureStateId: ArdoqId
): FutureStatePage$State => {
  if (state.status !== 'success' || state.futureState._id !== futureStateId) {
    return state;
  }
  return toDeletedErrorState('Future state deleted');
};

const applyUpdatedFutureState = (
  state: FutureStatePage$State,
  result: Result<FutureStateUpdatedResponse>
): FutureStatePage$State => {
  if (isArdoqError(result)) return state;
  if (
    state.status !== 'success' ||
    state.futureState._id !== result.futureState._id
  ) {
    return state;
  }
  return {
    ...state,
    futureState: result.futureState,
    owners: result.futureState.owners,
    assignedTo: result.futureState.assignedTo,
    enhancedScopeData: scopeDataOperations.enhance(result.scopeData),
  };
};

export const futureStatePage$ = reducedStream(
  'futureStatePage$',
  defaultState,
  [
    streamReducer(currentFutureState$, setFutureState),
    streamReducer(updateFutureState$, applyUpdatedFutureState),
    streamReducer(currentInitiativeSummary$, applyInitiativeSummary),
    streamReducer(
      websocket$.pipe(filterResourceType<Initiative>(ResourceType.INITIATIVE)),
      websocketReducer({
        delete: applyWebsocketInitiativeDelete,
      })
    ),
    streamReducer(
      websocket$.pipe(
        filterResourceType<FutureStateWebsocketEvent>(ResourceType.FUTURE_STATE)
      ),
      websocketReducer({
        update: applyFutureStateUpdate,
        delete: applyFutureStateDelete,
      })
    ),
  ]
);

const toFutureStateWriteAccess = (
  state: FutureStatePage$State
): TransformationsAccess =>
  state.status === 'success' ? state.permissions.writeInitiative : 'unknown';

export const futureStateWriteAccess$ = futureStatePage$.pipe(
  map(toFutureStateWriteAccess),
  distinctUntilChanged()
);
